import React, { useEffect, useMemo, useState } from 'react'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/client'
import Skeleton from 'react-loading-skeleton'
import moment from 'moment-timezone'
import './HistoryList.css'

const BQHistoryQuery = gql`
    query BQHistoryQuery($objectIds: [ID!]!, $limit: Int, $offset: Int) {
        bqHistory(objectIds: $objectIds, limit: $limit, offset: $offset) {
            hasMore
            items {
                entity
                entityId
                version
                eventTime
                actorName
                isNew
                isDeleted
                snapshot
                diff
            }
        }
    }
`

const formatJson = (value) => {
    if (value === null || value === undefined) return '--'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const getModelLabel = (entity) => {
    if (!entity) return '--'
    const [, modelName] = entity.split('.')
    return modelName || entity
}

const HIDDEN_CHANGE_FIELDS = new Set(['date_modified'])
const SNAPSHOT_FIELD_ALIASES = {
    modified_by: 'modified_by_id',
    created_by: 'created_by_id',
    assigned_by: 'assigned_by_id',
}

const formatFieldLabel = (fieldName) => {
    if (!fieldName) return '--'
    return fieldName
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '{ NULL }'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const normalizeForComparison = (value) => {
    if (value === null || value === undefined || value === '') return null
    if (Array.isArray(value)) return value.map(normalizeForComparison)
    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((acc, key) => {
                acc[key] = normalizeForComparison(value[key])
                return acc
            }, {})
    }
    return value
}

const areValuesEqual = (left, right) => (
    JSON.stringify(normalizeForComparison(left)) === JSON.stringify(normalizeForComparison(right))
)

const getSnapshotFieldName = (fieldName) => SNAPSHOT_FIELD_ALIASES[fieldName] || fieldName

function BQHistoryList({ objectIds = [], pageSize = 3, emptyText = 'No BigQuery history found yet.' }) {
    const [visibleDetails, setVisibleDetails] = useState({})
    const [loadedItems, setLoadedItems] = useState([])
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const normalizedObjectIds = useMemo(
        () => (objectIds || []).filter(Boolean),
        [objectIds]
    )
    const shouldShowSource = normalizedObjectIds.length > 1

    useEffect(() => {
        setVisibleDetails({})
        setLoadedItems([])
        setIsLoadingMore(false)
    }, [normalizedObjectIds.join('|')])

    const { data, loading, error, fetchMore } = useQuery(BQHistoryQuery, {
        variables: { objectIds: normalizedObjectIds, limit: pageSize, offset: 0 },
        skip: !normalizedObjectIds.length,
        fetchPolicy: 'cache-and-network',
        onCompleted: (result) => {
            setLoadedItems(result?.bqHistory?.items || [])
        },
    })

    const historyItems = loadedItems.length ? loadedItems : (data?.bqHistory?.items || [])
    const hasMore = !!data?.bqHistory?.hasMore
    const getItemKey = (item, index) => `${item.entity}-${item.entityId}-${item.version || index}`
    const shouldShowDetailsByDefault = (item) => item.isNew || item.isDeleted
    const toggleDetails = (key) => {
        setVisibleDetails((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    const handleLoadMore = async () => {
        setIsLoadingMore(true)
        try {
            const result = await fetchMore({
                variables: {
                    objectIds: normalizedObjectIds,
                    limit: pageSize,
                    offset: historyItems.length,
                },
            })

            const nextItems = result?.data?.bqHistory?.items || []
            setLoadedItems((prev) => [...prev, ...nextItems])
        } finally {
            setIsLoadingMore(false)
        }
    }

    if (loading && !historyItems.length) {
        return <Skeleton height={180} count={3} className="mb-2" />
    }

    if (error) {
        return <div className="alert alert-danger mb-0">{error.message}</div>
    }

    if (!historyItems.length) {
        return <p className="vgm-history-empty mb-0">{emptyText}</p>
    }

    return (
        <div className="vgm-history-list">
            {historyItems.map((item, index) => {
                const itemKey = getItemKey(item, index)
                const visibleChanges = item.diff && typeof item.diff === 'object'
                    ? Object.entries(item.diff).filter(([fieldName, oldValue]) => {
                        const snapshotFieldName = getSnapshotFieldName(fieldName)
                        return (
                            !HIDDEN_CHANGE_FIELDS.has(fieldName)
                            && !areValuesEqual(oldValue, item.snapshot?.[snapshotFieldName])
                        )
                    })
                    : []
                // const isDetailsVisible = visibleDetails[itemKey] ?? shouldShowDetailsByDefault(item)
                const isDetailsVisible = visibleDetails[itemKey]
                const cardClassName = [
                    'vgm-history-card',
                    item.isNew ? 'vgm-history-card-created' : '',
                    item.isDeleted ? 'vgm-history-card-deleted' : '',
                ].filter(Boolean).join(' ')
                return (
                    <div key={itemKey} className={cardClassName}>
                        <div className="vgm-history-card-header">
                            <div>
                                <div className="vgm-history-card-title">
                                    {shouldShowSource ? <>
                                        {getModelLabel(item.entity)} #{item.entityId}
                                    </> : <>
                                        Version {item.version || '--'}
                                    </>}
                                </div>
                                <div className="vgm-history-card-meta">
                                    {item.eventTime ? moment(item.eventTime).tz('America/New_York').format('lll') : '--'}
                                    {' | '}
                                    {item.actorName || 'System'}
                                </div>
                            </div>
                            <div className="vgm-history-badges">
                                {item.isNew && <span className="vgm-history-badge vgm-history-badge-new">Created</span>}
                                {item.isDeleted && <span className="vgm-history-badge vgm-history-badge-deleted">Deleted</span>}
                            </div>
                        </div>

                        {!item.isNew && !item.isDeleted && <div className="vgm-history-section">
                            <div className="vgm-history-section-label">Changes</div>
                            {visibleChanges.length > 0 ? (
                                <div className="vgm-history-change-list">
                                    {visibleChanges.map(([fieldName, oldValue]) => {
                                        const snapshotFieldName = getSnapshotFieldName(fieldName)
                                        return (
                                            <div key={fieldName} className="vgm-history-change-row">
                                                <div className="vgm-history-change-field">{formatFieldLabel(fieldName)}</div>
                                                <div className="vgm-history-change-inline">
                                                    <span className="vgm-history-change-chip vgm-history-change-chip-old">
                                                        {/* <span className="vgm-history-change-chip-label">Before</span> */}
                                                        <span className="vgm-history-change-chip-value">{formatValue(oldValue)}</span>
                                                    </span>
                                                    <span className="vgm-history-change-arrow">
                                                        <i className="fa fa-arrow-right" />
                                                    </span>
                                                    <span className="vgm-history-change-chip vgm-history-change-chip-new">
                                                        {/* <span className="vgm-history-change-chip-label">After</span> */}
                                                        <span className="vgm-history-change-chip-value">
                                                            {formatValue(item.snapshot?.[snapshotFieldName])}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            ) : (
                                <div className="vgm-history-empty">No changes found for this record.</div>
                            )}
                        </div>}

                        <button type="button" className="vgm-history-toggle" onClick={() => toggleDetails(itemKey)}>
                            {isDetailsVisible
                                ? 'Hide Record Details'
                                : 'View Record Details'}
                        </button>

                        {isDetailsVisible && (
                            <div className="vgm-history-section">
                                <div className="vgm-history-section-label">Record Details</div>
                                <pre className="vgm-history-json mb-0">{formatJson(item.snapshot)}</pre>
                            </div>
                        )}
                    </div>
                )
            })}

            {hasMore && (
                <button
                    type="button"
                    className="vgm-history-load-more"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                >
                    {isLoadingMore ? 'Loading...' : 'Load More'}
                </button>
            )}
        </div>
    )
}

export default BQHistoryList
